import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  makeStateKey,
  PendingTasks,
  signal,
  TransferState,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { SvgComponent } from '@shared/components/svg';
import { Time } from '@shared/constants';
import { HandleHtmlLinksDirective } from '@shared/directives';
import { DataLayerService } from '@shared/services/data-layer';
import { Promotion, PromotionBanner } from '@shared/services/elastic-search/models';
import { CurrentLanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { PageStore, PromotionStore } from '@stores';
import { finalize, firstValueFrom, take, timeout } from 'rxjs';

// The <head> script of index.html reads these names to hide a static page's banner before its first paint.
export const PROMO_BANNER_HIDDEN_CLASS = 'promo-banner-hidden';
export const PROMO_BANNER_CLOSED_UNTIL_KEY = 'promoBannerClosedUntil';
export const PROMO_START_ATTRIBUTE = 'data-promo-start';
export const PROMO_END_ATTRIBUTE = 'data-promo-end';
const SERVER_READ_TIMEOUT_MS = 2000;

// The banner without its text: see « Bandeau promo » in `docs/architecture.md`.
type BannerFrame = Pick<PromotionBanner, 'url' | 'excludePageType'> & Pick<Promotion, 'dateStart' | 'dateEnd'>;

const isPromotionRunning = ({ dateStart, dateEnd }: BannerFrame, now: number): boolean =>
  (dateStart ?? 0) <= now && now < (dateEnd ?? Infinity);

@Component({
  selector: 'app-promotion',
  imports: [SvgComponent, RouterLink, HandleHtmlLinksDirective],
  templateUrl: './promotion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromotionComponent {
  private readonly dataLayerService = inject(DataLayerService);
  private readonly promotionStore = inject(PromotionStore);
  protected readonly banner = this.promotionStore.select('banner');
  private readonly dateStart = this.promotionStore.select('dateStart');
  private readonly dateEnd = this.promotionStore.select('dateEnd');
  private readonly serverFrame = signal<BannerFrame | null>(null);
  protected readonly frame = computed<BannerFrame | null>(() => {
    const banner = this.banner();
    return banner?.text ? { ...banner, dateStart: this.dateStart(), dateEnd: this.dateEnd() } : this.serverFrame();
  });
  private readonly pageType = inject(PageStore).selectPath((state) => state.type);
  private readonly isClosed = signal(false);
  protected readonly isVisible = computed(() => {
    const frame = this.frame();
    return (
      !!frame &&
      !frame.excludePageType?.includes(this.pageType()!) &&
      !this.isClosed() &&
      isPromotionRunning(frame, Date.now())
    );
  });
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly logs = inject(LogsService);
  private readonly transferState = inject(TransferState);
  private readonly frameKey = makeStateKey<BannerFrame>(
    `${inject(CurrentLanguageService).language.langcode}:promotion:banner-frame`
  );

  constructor() {
    if (inject(PlatformContextService).isServer()) {
      const done = inject(PendingTasks).add();
      void this.renderServerFrame().finally(done);
      return;
    }
    this.isClosed.set(readClosedUntil() > Date.now());
    this.serverFrame.set(this.transferState.get(this.frameKey, null));
    effect(() => this.document.documentElement.classList.toggle(PROMO_BANNER_HIDDEN_CLASS, !this.isVisible()));
    this.readPromotion();
  }

  close(): void {
    try {
      localStorage.setItem(PROMO_BANNER_CLOSED_UNTIL_KEY, String(Date.now() + Time.FIVE_DAYS * 1000));
    } catch (err) {
      this.logs.warn('PromotionComponent', 'Unable to remember the closed banner', err);
    }
    this.isClosed.set(true);
  }

  protected emitGtmEvent(): void {
    const gtm = this.banner()?.gtmConfig;
    if (gtm?.event && gtm?.event.length > 0) {
      this.dataLayerService.push({
        event: gtm?.event,
        group: gtm?.group,
        description: gtm?.description,
        context: gtm?.context,
        button_text_value: this.banner()?.text,
        event_type: 'click',
      });
    }
  }

  private async renderServerFrame(): Promise<void> {
    try {
      const { ElasticSearchService } = await import('@shared/services/elastic-search');
      const { banner, dateStart, dateEnd } = await firstValueFrom(
        this.injector.get(ElasticSearchService).getPromotion().pipe(timeout(SERVER_READ_TIMEOUT_MS))
      );
      if (!banner?.text) return;
      const frame: BannerFrame = { url: banner.url, excludePageType: banner.excludePageType, dateStart, dateEnd };
      this.transferState.set(this.frameKey, frame);
      this.serverFrame.set(frame);
      const html = this.document.documentElement;
      if (dateStart) html.setAttribute(PROMO_START_ATTRIBUTE, String(dateStart));
      if (dateEnd) html.setAttribute(PROMO_END_ATTRIBUTE, String(dateEnd));
    } catch (err) {
      this.logs.error('PromotionComponent', 'Rendering the page without the promo banner', err);
    }
  }

  private readPromotion(): void {
    void import('@shared/services/elastic-search')
      .then(({ ElasticSearchService }) => {
        this.injector
          .get(ElasticSearchService)
          .getPromotion()
          .pipe(
            take(1),
            finalize(() => this.serverFrame.set(null))
          )
          .subscribe({
            next: (promotion) => this.promotionStore.set(promotion),
            error: (err) => this.logs.error('PromotionComponent', 'getPromotion failed', err),
          });
      })
      .catch((err: unknown) => {
        this.serverFrame.set(null);
        this.logs.error('PromotionComponent', 'Failed to load elastic-search chunk', err);
      });
  }
}

function readClosedUntil(): number {
  try {
    return Number(localStorage.getItem(PROMO_BANNER_CLOSED_UNTIL_KEY));
  } catch {
    return 0;
  }
}
